import {
  useState, useEffect, useRef
} from 'react';

import {
  collection, doc, onSnapshot, updateDoc, deleteField,
  getDoc, setDoc, deleteDoc, getDocs, query, where,
  arrayUnion, arrayRemove
} from 'firebase/firestore';
import db from '@services/firebase-service';
import { useStreamData } from '@lib/hooks/useStreamData';

const useFirestoreSync = ({
  start,
  stopFeature,
  featureNextState,
  featurePrevState,
  handleLinkFeature,
  toggleShowIpaEventJoinDetailsModal,
  streamId,
  authToken,
  currentIndexRef,
  currentRosFeature,
  setModerationMode,
  setCurrentIndex,
  handleQuickQuestions,
  handleLinkLibrary,
  handleTextTrack,
  handleFullScreen,
  handleActiveInteraction,
  setFeatureQuestion,
  handleLinkRos,
  handleUnlinkRos,
  disconnect
}) => {
  const [firestoreUnsub, setFirestoreUnsub] = useState({ unsub: null });
  const [, _setActiveInteraction] = useState();

  const [meta, _setMeta] = useState();

  const activeInteractionRef = useRef();
  const setActiveInteraction = (data) => {
    activeInteractionRef.current = data;
    _setActiveInteraction(data);
  };

  const metaRef = useRef();
  const setMeta = (data) => {
    metaRef.current = data;
    _setMeta(data);
  };

  const { dispatchLinkLibrary } = useStreamData(s => ({ dispatchLinkLibrary: s.dispatchLinkLibrary, linkLibraryState: s.linkLibraryState }));

  const getFSId = () => (authToken?.authType === 'ipa' ? authToken?.copilotId : authToken?.id);

  const runFSCommand = (cmd) => {
    if (cmd.initiator === authToken?.id || cmd.cmd === 'ignore') return;

    if (cmd.cmd === 'start') {
      start();
    }
    if (cmd.cmd === 'stop') {
      stopFeature();
    }
    if (cmd.cmd === 'restart') {
      start();
    }
    if (cmd.cmd === 'next') {
      setCurrentIndex(cmd.payload.currentIndex);
      featureNextState();
    }
    if (cmd.cmd === 'prev') {
      setCurrentIndex(cmd.payload.currentIndex);
      featurePrevState();
    }
    if (cmd.cmd === 'handleLinkFeature') {
      handleLinkFeature(cmd.payload.feature, cmd.payload.index);
    }
    if (cmd.cmd === 'setAdhocFeature') {
      setFeatureQuestion({ ...cmd.payload, pushedToFS: true });
    }

    if (cmd.cmd === 'toggleQR') {
      toggleShowIpaEventJoinDetailsModal(cmd.payload.flag);
    }
    if (cmd.cmd === 'runQQ') {
      handleQuickQuestions();
    }
    if (cmd.cmd === 'runLL') {
      handleLinkLibrary();
    }
    if (cmd.cmd === 'runTTR') {
      handleTextTrack();
    }
    if (cmd.cmd === 'toggleFullScreen') {
      handleFullScreen();
    }
    if (cmd.cmd === 'linkRos') {
      handleLinkRos(cmd.payload.title, cmd.payload.interactions);
    }
    if (cmd.cmd === 'unlinkRos') {
      handleUnlinkRos();
    }
    if (cmd.cmd === 'disconnect') {
      disconnect();
    }
    if (cmd.cmd === 'setLinkLibraryFilters') {
      dispatchLinkLibrary({ type: 'UPDATE_FILTERS', filters: { ...cmd.payload.filters } });
    }
    if (cmd.cmd === 'deleteLinkLibraryLink') {
      dispatchLinkLibrary({ type: 'DELETE_LINK', link: { ...cmd.payload.link } });
    }
  };

  const syncCollRef = collection(db, 'copilot-syncs', streamId, 'copilots');

  const updateCopilotSync = async (data) => {
    const q = query(syncCollRef, where('actor', '!=', getFSId()));
    getDocs(q).then((querySnapshot) => {
      querySnapshot.forEach((copilotDocRef) => {
        const currRef = doc(syncCollRef, copilotDocRef.id);
        if (data.cmd) {
          updateDoc(currRef, { cmds: arrayUnion({ ...data.cmd }) });
        } else {
          updateDoc(currRef, { ...data });
        }
      });
    });
  };

  const updateFSMeta = async (metaData) => {
    updateCopilotSync({ meta: metaData });
  };

  const pushToFS = async (data) => {
    updateCopilotSync(data);
  };

  const pushCmdToFS = (cmd, payload = {}) => {
    updateCopilotSync({
      cmd: {
        currentIndex: currentIndexRef.current,
        actor: getFSId(),
        timestamp: Date.now(),
        cmd,
        payload
      }
    });
  };

  const unsubscribeFromFirestore = () => {
    if (firestoreUnsub.unsub) {
      firestoreUnsub.unsub();
    }
  };

  useEffect(() => {
    unsubscribeFromFirestore();
    setMeta();
    setActiveInteraction();

    const copilotRef = doc(syncCollRef, getFSId());

    setFirestoreUnsub({
      unsub: onSnapshot(copilotRef, (metaDocRef) => {
        if (metaDocRef.exists()) {
          const data = metaDocRef.data();
          const cleanupData = {};
          let activeInt;

          if (data.meta) {
            setMeta(data.meta);
            cleanupData.meta = deleteField();
          }

          if (data?.meta?.activeInteraction) {
            setModerationMode(true);
            activeInt = data.meta.activeInteraction;
            cleanupData.meta = deleteField();
            if (handleActiveInteraction) {
              handleActiveInteraction(activeInt);
            } else {
              setActiveInteraction(activeInt);
            }
          }

          if (data.cmds && data.actor !== getFSId()) {
            const cmdsToRemove = [];
            setModerationMode(true);
            data.cmds.sort((a, b) => a.timestamp - b.timestamp);
            data.cmds.forEach((cmd) => {
              runFSCommand(cmd);
              cmdsToRemove.push(cmd);
            });
            cleanupData.cmds = arrayRemove(...cmdsToRemove);
          } else {
            setModerationMode(false);
          }

          if (Object.keys(cleanupData).length > 0) {
            updateDoc(copilotRef, cleanupData);
          }
        }
      })
    });

    return unsubscribeFromFirestore;
  }, [currentRosFeature]);

  // Set initial document is not present.
  useEffect(() => {
    const copilotRef = doc(syncCollRef, getFSId());
    const cleanup = () => {
      deleteDoc(copilotRef);
    };

    getDoc(copilotRef).then((docSnap) => {
      if (!docSnap.exists()) {
        setDoc(copilotRef, {
          actor: getFSId(),
          timestamp: Date.now(),
          cmds: []
        });
      } else {
        updateDoc(copilotRef, {
          timestamp: Date.now(),
          cmds: []
        });
      }
    });

    return () => cleanup();
  }, []);

  return {
    activeInteractionRef,
    runFSCommand,
    pushCmdToFS,
    getFSId,
    updateFSMeta,
    metaRef,
    meta,
    pushToFS,
    setActiveInteraction
  };
};

export default useFirestoreSync;
